
/**
 * `null` or `undefined` should not be assignable to `T`.
 */
export class Optional<T> {
  private constructor(
    public readonly isSome: boolean,
    private readonly _value: T | null = null,
  ) {}

  static either<T>(value: T | null): Optional<T> {
    return value != null ? new Optional(true, value) : new Optional(false);
  }

  static some<T>(value: T): Optional<T> {
    return new Optional(true, value);
  }

  static none<T>(): Optional<T> {
    return new Optional(false);
  }

  get isNone(): boolean {
    return !this.isSome;
  }

  get value(): T {
    if(this.isNone) {
      throw new Error('value is not accessible on a none instance');
    }

    if(this._value == null) {
      throw new Error(`${this._value} found on a some instance`);
    }

    return this._value;
  }

  get valueOrNull(): T | null {
    return this._value;
  }

  valueOrDefault(defaultValue: T): T {
    return this.isSome ? this.value : defaultValue;
  }
}

export class QueryableOptionalString {
  static readonly NULL: string = 'null';

  private readonly _stringValue: string | null;
  constructor(
      stringValue: string | null | undefined = null,
  ) {
    switch(stringValue) {
      // eslint-disable-next-line no-undefined
      case undefined:
      case null:
      case '':
      case QueryableOptionalString.NULL:
        this._stringValue = null;
        break;
      default:
        this._stringValue = stringValue;
        break;
    }
  }

  static readonly none = new QueryableOptionalString();

  get hasValue(): boolean {
    return this._stringValue !== null;
  }

  get value(): string | null {
    return this._stringValue;
  }

  get queryableValue(): string {
    return this.value ?? QueryableOptionalString.NULL;
  }

  clone(): this {
    return new QueryableOptionalString(this._stringValue) as this;
  }

  isSame(other: QueryableOptionalString): boolean {
    return other._stringValue === this._stringValue;
  }

  equals(str: string): boolean {
    return str === this._stringValue;
  }
}
